import type { DeliveryQueueFailureReason, DeliveryQueueItem } from '../services/firebaseFunctions';

export interface DeliveryQueueGroup {
  key: string;
  companyId: string;
  companyName: string | null;
  productId: string;
  productName: string | null;
  productType: 'ebook' | 'audiobook' | null;
  /**
   * Mínimo de ativos a adicionar na lista para resolver todas as pendências do grupo:
   * como um usuário não recebe o mesmo título duas vezes, é o máximo, entre os CPFs,
   * da soma de quantityPending daquele CPF no grupo.
   */
  minRequired: number;
  itemCount: number;
  failureReasons: DeliveryQueueFailureReason[];
  items: DeliveryQueueItem[];
}

export function groupDeliveryQueueItems(items: DeliveryQueueItem[]): DeliveryQueueGroup[] {
  const groups = new Map<string, DeliveryQueueGroup>();

  for (const item of items) {
    const key = `${item.companyId}__${item.productId}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        companyId: item.companyId,
        companyName: item.companyName,
        productId: item.productId,
        productName: item.productName,
        productType: item.productType,
        minRequired: 0,
        itemCount: 0,
        failureReasons: [],
        items: [],
      };
      groups.set(key, group);
    }
    group.companyName = group.companyName ?? item.companyName;
    group.productName = group.productName ?? item.productName;
    group.productType = group.productType ?? item.productType;
    group.itemCount += 1;
    group.items.push(item);
    if (!group.failureReasons.includes(item.failureReason)) {
      group.failureReasons.push(item.failureReason);
    }
  }

  const result = Array.from(groups.values());

  for (const group of result) {
    const pendingByCpf = new Map<string, number>();
    for (const item of group.items) {
      pendingByCpf.set(item.cpf, (pendingByCpf.get(item.cpf) ?? 0) + item.quantityPending);
    }
    group.minRequired = Math.max(0, ...Array.from(pendingByCpf.values()));
    group.items.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  return result.sort((a, b) => {
    const companyCompare = (a.companyName ?? a.companyId).localeCompare(
      b.companyName ?? b.companyId,
      'pt-BR',
    );
    if (companyCompare !== 0) return companyCompare;
    return (a.productName ?? a.productId).localeCompare(b.productName ?? b.productId, 'pt-BR');
  });
}
